import type { MonthlyClose, MonthlyCloseStatus } from '@eriko/shared-types';
import { formatCurrency } from '@eriko/shared-types';
import { Badge } from '@eriko/ui/src/components/ui/badge';
import { Button } from '@eriko/ui/src/components/ui/button';
import { DataTable } from '@eriko/ui/src/components/ui/data-table';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDeleteMonthlyClose, useOrgMonthlyCloses } from '../../../hooks/useReview';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function statusVariant(status: MonthlyCloseStatus) {
  switch (status) {
    case 'READY_FOR_REVIEW':
      return 'warning' as const;
    case 'IN_REVIEW':
      return 'info' as const;
    case 'APPROVED':
    case 'DELIVERED':
      return 'success' as const;
    case 'PROCESSING':
      return 'secondary' as const;
    default:
      return 'outline' as const;
  }
}

const col = createColumnHelper<MonthlyClose>();

function makeColumns(onDelete: (close: MonthlyClose) => void) {
  return [
  col.accessor((row) => `${MONTH_NAMES[row.month - 1]} ${row.year}`, {
    id: 'period',
    header: 'Period',
    cell: (info) => <span className="font-semibold text-surface-900">{info.getValue()}</span>,
  }),
  col.accessor('status', {
    header: 'Status',
    cell: (info) => (
      <Badge variant={statusVariant(info.getValue())}>{info.getValue().replace(/_/g, ' ')}</Badge>
    ),
  }),
  col.accessor('totalRevenue', {
    header: () => <span className="text-right block">Revenue</span>,
    cell: (info) => {
      const v = info.getValue();
      const cur = info.row.original.currency ?? undefined;
      return (
        <span className="block text-right font-mono text-[11px] text-surface-600">
          {v != null ? formatCurrency(v, 0, cur) : '—'}
        </span>
      );
    },
  }),
  col.accessor('totalExpenses', {
    header: () => <span className="text-right block">Expenses</span>,
    cell: (info) => {
      const v = info.getValue();
      const cur = info.row.original.currency ?? undefined;
      return (
        <span className="block text-right font-mono text-[11px] text-surface-600">
          {v != null ? formatCurrency(v, 0, cur) : '—'}
        </span>
      );
    },
  }),
  col.accessor('netProfit', {
    header: () => <span className="text-right block">Net Profit</span>,
    cell: (info) => {
      const v = info.getValue();
      const cur = info.row.original.currency ?? undefined;
      return (
        <span className="block text-right font-mono text-[11px] font-medium text-pos">
          {v != null ? formatCurrency(v, 0, cur) : '—'}
        </span>
      );
    },
  }),
  col.accessor('transactionCount', {
    header: () => <span className="text-center block">Txns</span>,
    cell: (info) => (
      <span className="block text-center font-mono text-[11px] text-surface-600">
        {info.getValue() ?? '—'}
      </span>
    ),
  }),
  col.accessor('flagCount', {
    header: () => <span className="text-center block">Flags</span>,
    cell: (info) => {
      const v = info.getValue();
      return (
        <span
          className={`block text-center font-mono text-[10px] font-semibold ${v ? 'text-neg' : 'text-surface-400'}`}
        >
          {v || '—'}
        </span>
      );
    },
  }),
  col.display({
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    size: 40,
    cell: (info) => {
      const row = info.row.original;
      return (
        <Button
          variant="ghost"
          size="icon"
          className="text-surface-400 hover:text-neg hover:bg-neg-surface"
          title="Delete this month's books"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row);
          }}
        >
          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
        </Button>
      );
    },
  }),
  ];
}

const MONTH_NAME_LABEL = (y: number, m: number) => `${MONTH_NAMES[m - 1]} ${y}`;

export function BooksTab({ orgId }: { orgId: string }) {
  const navigate = useNavigate();
  const { data: closes, isLoading } = useOrgMonthlyCloses(orgId);
  const deleteClose = useDeleteMonthlyClose();
  const [pendingDelete, setPendingDelete] = useState<MonthlyClose | null>(null);

  const data = useMemo(() => closes ?? [], [closes]);

  const columns = useMemo(
    () => makeColumns((close) => setPendingDelete(close)),
    [],
  );
  // TanStack Table's useReactTable returns memoizable values that React Compiler
  // flags as "incompatible-library" — known false positive. Suppression is
  // per-callsite; safe to remove once the rule recognizes the library.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deleteClose.mutate(
      { orgId, closeId: pendingDelete.id },
      {
        onSuccess: () => setPendingDelete(null),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-surface-900 border-t-transparent" />
      </div>
    );
  }
  return (
    <>
      <DataTable
        table={table}
        onRowClick={(row) => navigate(`/orgs/${orgId}/close/${row.id}`)}
        emptyMessage="No monthly closes found."
      />
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => !deleteClose.isPending && setPendingDelete(null)}
        >
          <div
            className="bg-surface-0 border border-surface-200 rounded-lg shadow-xl p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-surface-900">
              Delete {MONTH_NAME_LABEL(pendingDelete.year, pendingDelete.month)} books?
            </h3>
            <p className="mt-2 text-xs text-surface-600 leading-relaxed">
              This permanently deletes the monthly close, its {pendingDelete.transactionCount ?? 0}{' '}
              transactions, all transaction flags, and any ledger entries for this period. This
              cannot be undone.
            </p>
            {(pendingDelete.status === 'APPROVED' || pendingDelete.status === 'DELIVERED') && (
              <p className="mt-2 text-xs text-warn font-medium">
                ⚠ This close has been signed off. Only a platform admin can delete it.
              </p>
            )}
            {deleteClose.error && (
              <p className="mt-2 text-xs text-neg">
                {(deleteClose.error as Error).message}
              </p>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPendingDelete(null)}
                disabled={deleteClose.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-neg text-surface-0 hover:bg-neg/90"
                onClick={confirmDelete}
                disabled={deleteClose.isPending}
              >
                {deleteClose.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
